import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  CheckCircle, 
  Star, 
  Home,
  ArrowRight,
  CreditCard,
  Calendar,
  Users,
  Shield
} from 'lucide-react';

const UpgradeSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [upgradeDetails, setUpgradeDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  const sessionId = searchParams.get('session_id');
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (sessionId) {
      completeUpgrade();
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  const completeUpgrade = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/upgrades/complete/${sessionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setUpgradeDetails({
          newMembershipLevel: data.new_membership_level,
          message: data.message,
          redirectUrl: data.redirect_url
        });
        
        // Update localStorage with new membership level
        const storedData = localStorage.getItem('basicMemberApplication');
        if (storedData) {
          const memberData = JSON.parse(storedData);
          memberData.membershipLevel = data.new_membership_level;
          localStorage.setItem('basicMemberApplication', JSON.stringify(memberData));
        }
      } else {
        throw new Error(data.detail || 'Failed to complete upgrade');
      }
    } catch (error) {
      console.error('Error completing upgrade:', error);
      setUpgradeDetails({
        error: 'Failed to complete upgrade. Please contact support.',
        message: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const getMembershipIcon = (level) => {
    switch (level) {
      case 'Standard':
        return <Home className="w-8 h-8 text-amber-600" />;
      case 'Premium':
        return <Users className="w-8 h-8 text-blue-600" />;
      default:
        return <Star className="w-8 h-8 text-green-600" />;
    }
  };

  const getMembershipColor = (level) => {
    switch (level) {
      case 'Standard':
        return 'amber';
      case 'Premium':
        return 'blue';
      default:
        return 'green';
    }
  };

  const getMembershipBenefits = (level) => {
    const baseBenefits = [
      "All Basic membership benefits",
      "General Contractor & Skilled Laborers Network access",
      "Member community forum"
    ];

    if (level === 'Standard') {
      return [
        ...baseBenefits,
        "Property management portal",
        "Tenant payment coordination", 
        "Maintenance request system",
        "Monthly financial reports"
      ];
    }

    if (level === 'Premium') {
      return [
        ...baseBenefits,
        "Everything in Standard, plus:",
        "📊 Reporting Tools",
        "💼 Accounting Software Integration",
        "🏛️ Land Trust Education and Support",
        "⚖️ Eviction Support",
        "Full administrative support",
        "Priority member support"
      ];
    }

    return baseBenefits;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-lg text-slate-600">Processing your upgrade...</p>
        </div>
      </div>
    );
  }

  if (upgradeDetails?.error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center">
        <Card className="max-w-md mx-auto">
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">❌</span>
            </div>
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Upgrade Failed</h2>
            <p className="text-slate-600 mb-4">{upgradeDetails.error}</p>
            <Button onClick={() => navigate('/basic-portal')} className="w-full">
              Return to Portal
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const membershipLevel = upgradeDetails?.newMembershipLevel || 'Standard';
  const colorScheme = getMembershipColor(membershipLevel);

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-green-600 mr-3" />
              <h1 className="text-xl font-semibold text-gray-900">
                Legacy Property Association
              </h1>
            </div>
            <Badge className={`bg-${colorScheme}-100 text-${colorScheme}-800`}>
              {membershipLevel} Member
            </Badge>
          </div>
        </div>
      </div>

      {/* Success Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success Banner */}
        <Card className={`bg-gradient-to-r from-${colorScheme}-600 to-${colorScheme}-700 text-white mb-8`}>
          <CardContent className="p-8">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center mb-4">
                  <CheckCircle className="w-8 h-8 text-green-200 mr-3" />
                  <h2 className="text-3xl font-bold">Upgrade Successful! 🎉</h2>
                </div>
                <p className={`text-${colorScheme}-100 text-lg mb-2`}>
                  Welcome to {membershipLevel} membership! Your recurring billing has been set up successfully.
                </p>
                <p className={`text-${colorScheme}-200`}>
                  Your enhanced member portal and benefits are now active.
                </p>
              </div>
              <div className="hidden md:block">
                {getMembershipIcon(membershipLevel)}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Membership Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                {getMembershipIcon(membershipLevel)}
                <span>{membershipLevel} Membership Details</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="font-medium text-slate-700">Membership Level:</span>
                  <Badge className={`bg-${colorScheme}-100 text-${colorScheme}-800`}>
                    {membershipLevel}
                  </Badge>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="font-medium text-slate-700">Billing:</span>
                  <div className="flex items-center space-x-1">
                    <CreditCard className="w-4 h-4 text-slate-500" />
                    <span>${membershipLevel === 'Standard' ? '50' : '100'}/month</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="font-medium text-slate-700">Next Billing:</span>
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4 text-slate-500" />
                    <span>{new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}</span>
                  </div>
                </div>
                
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-800 font-medium">
                      Automatic recurring billing active
                    </span>
                  </div>
                  <p className="text-xs text-green-700 mt-1">
                    Your membership will automatically renew each month. You can manage your subscription anytime.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* New Benefits */}
          <Card>
            <CardHeader>
              <CardTitle>Your New Benefits</CardTitle>
              <p className="text-slate-600">Everything included in your {membershipLevel} membership:</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {getMembershipBenefits(membershipLevel).map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <CheckCircle className={`w-4 h-4 text-${colorScheme}-600 flex-shrink-0`} />
                    <span className="text-sm text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Next Steps */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>What's Next?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <div className={`w-12 h-12 bg-${colorScheme}-100 rounded-full flex items-center justify-center mx-auto mb-3`}>
                  <span className="text-lg">1️⃣</span>
                </div>
                <h4 className="font-semibold text-slate-800 mb-2">Access Your Portal</h4>
                <p className="text-sm text-slate-600 mb-3">
                  Explore your enhanced member portal with new tools and features.
                </p>
              </div>
              
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <div className={`w-12 h-12 bg-${colorScheme}-100 rounded-full flex items-center justify-center mx-auto mb-3`}>
                  <span className="text-lg">2️⃣</span>
                </div>
                <h4 className="font-semibold text-slate-800 mb-2">Set Up Properties</h4>
                <p className="text-sm text-slate-600 mb-3">
                  Add your properties to start tracking income, expenses, and tenants.
                </p>
              </div>
              
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <div className={`w-12 h-12 bg-${colorScheme}-100 rounded-full flex items-center justify-center mx-auto mb-3`}>
                  <span className="text-lg">3️⃣</span>
                </div>
                <h4 className="font-semibold text-slate-800 mb-2">Explore Features</h4>
                <p className="text-sm text-slate-600 mb-3">
                  {membershipLevel === 'Premium' ? 'Download your first report for IRS filing compliance and explore administrative support.' : 'Use tenant coordination tools and maintenance request system.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 mt-8">
          <Button 
            onClick={() => {
              if (upgradeDetails?.redirectUrl) {
                window.open(upgradeDetails.redirectUrl, '_blank');
              } else {
                window.open(process.env.REACT_APP_PROPTECH_LOGIN_URL, '_blank');
              }
            }}
            className={`flex-1 bg-${colorScheme}-600 hover:bg-${colorScheme}-700`}
          >
            Access Your {membershipLevel} Portal
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          
          <Button 
            variant="outline" 
            onClick={() => navigate('/basic-portal')}
            className="flex-1"
          >
            Return to Basic Portal
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UpgradeSuccess;